import {
  BillingPlan,
  SspStatus,
  SsuStatus,
  type BillingPlan as BillingPlanValue,
  type SspId,
  type SspStatus as SspStatusValue,
  type SsuId,
  type SsuStatus as SsuStatusValue,
} from '@cguard/types';

export interface SspSummary {
  readonly id: SspId;
  readonly name: string;
  readonly contactEmail: string;
  readonly status: SspStatusValue;
  readonly billingPlan: BillingPlanValue;
  readonly contractStartUtc: string;
  readonly createdAtUtc: string;
  readonly suspendedAtUtc: string | null;
  readonly decommissionedAtUtc: string | null;
  readonly suspensionReason: string | null;
  readonly ssuCount: number;
  readonly siteCount: number;
  readonly edgeOnline: number;
  readonly edgeTotal: number;
  readonly slaAttainment30d: number;
}

export interface SsuSummary {
  readonly id: SsuId;
  readonly owningSspId: SspId;
  readonly legalName: string;
  readonly tradeName: string;
  readonly contactEmail: string;
  readonly billingAddress: string;
  readonly status: SsuStatusValue;
  readonly contractStartUtc: string;
  readonly createdAtUtc: string;
  readonly offboardedAtUtc: string | null;
}

export interface SsuOnboardingInput {
  readonly owningSspId: SspId;
  readonly legalName: string;
  readonly tradeName: string;
  readonly contactEmail: string;
  readonly billingAddress: string;
  readonly contractStartUtc: string;
  readonly reason: string;
}

export interface SspAuditSummary {
  readonly id: string;
  readonly at: string;
  readonly actor: string;
  readonly action: string;
  readonly targetType: string;
  readonly targetId: string;
  readonly outcome: 'success' | 'denied' | 'failed';
}

export interface SspDetailSummary {
  readonly ssp: SspSummary;
  readonly ssus: readonly SsuSummary[];
  readonly audit: readonly SspAuditSummary[];
  readonly contract: {
    readonly startUtc: string;
    readonly billingPlan: BillingPlanValue;
  };
}

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function normaliseSspPage(value: unknown): SspSummary[] {
  const page = requireRecord(value, 'SSP page');
  if (!Array.isArray(page.items)) {
    throw new Error('Central returned an SSP page without items.');
  }
  return page.items.map((item, index) => normaliseSspSummary(item, `SSP ${index + 1}`));
}

export function normaliseSsuPage(value: unknown): SsuSummary[] {
  const page = requireRecord(value, 'SSU page');
  if (!Array.isArray(page.items)) {
    throw new Error('Central returned an SSU page without items.');
  }
  return page.items.map((item, index) => normaliseSsuSummary(item, `SSU ${index + 1}`));
}

export function normaliseSspSummary(value: unknown, label = 'SSP'): SspSummary {
  const row = requireRecord(value, label);
  return {
    id: requireSspId(row.sspId, `${label} id`),
    name: requireString(row.name, `${label} name`),
    contactEmail: requireString(row.contactEmail, `${label} contact email`),
    status: requireSspStatus(row.status, `${label} status`),
    billingPlan: requireBillingPlan(row.billingPlan, `${label} billing plan`),
    contractStartUtc: requireTimestamp(row.contractStartUtc, `${label} contract start`),
    createdAtUtc: requireTimestamp(row.createdAtUtc, `${label} creation timestamp`),
    suspendedAtUtc: nullableTimestamp(row.suspendedAtUtc, `${label} suspension timestamp`),
    decommissionedAtUtc: nullableTimestamp(
      row.decommissionedAtUtc,
      `${label} decommission timestamp`,
    ),
    suspensionReason: nullableString(row.suspensionReason, `${label} suspension reason`),
    ssuCount: requireNonNegativeInteger(row.ssuCount, `${label} SSU count`),
    siteCount: requireNonNegativeInteger(row.siteCount, `${label} site count`),
    edgeOnline: requireNonNegativeInteger(row.edgeOnline, `${label} online edge count`),
    edgeTotal: requireNonNegativeInteger(row.edgeTotal, `${label} total edge count`),
    slaAttainment30d: requireNonNegativeNumber(row.slaAttainment30d, `${label} SLA attainment`),
  };
}

export function normaliseSsuSummary(value: unknown, label = 'SSU'): SsuSummary {
  const row = requireRecord(value, label);
  return {
    id: requireSsuId(row.ssuId, `${label} id`),
    owningSspId: requireSspId(row.owningSspId, `${label} owning SSP id`),
    legalName: requireString(row.legalName, `${label} legal name`),
    tradeName: requireString(row.tradeName, `${label} trade name`),
    contactEmail: requireString(row.contactEmail, `${label} contact email`),
    billingAddress: requireString(row.billingAddress, `${label} billing address`),
    status: requireSsuStatus(row.status, `${label} status`),
    contractStartUtc: requireTimestamp(row.contractStartUtc, `${label} contract start`),
    createdAtUtc: requireTimestamp(row.createdAtUtc, `${label} creation timestamp`),
    offboardedAtUtc: nullableTimestamp(row.offboardedAtUtc, `${label} offboard timestamp`),
  };
}

export function normaliseSspDetail(value: unknown): SspDetailSummary {
  const detail = requireRecord(value, 'SSP detail');
  if (!Array.isArray(detail.ssus)) {
    throw new Error('Central returned SSP detail without an SSU list.');
  }
  if (!Array.isArray(detail.audit)) {
    throw new Error('Central returned SSP detail without an audit list.');
  }

  const contract = requireRecord(detail.contract, 'SSP contract');
  return {
    ssp: normaliseSspSummary(detail.ssp, 'SSP detail record'),
    ssus: detail.ssus.map((item, index) =>
      normaliseSsuSummary(item, `SSP detail SSU ${index + 1}`),
    ),
    audit: detail.audit.map((item, index) => normaliseAudit(item, index)),
    contract: {
      startUtc: requireTimestamp(contract.startUtc, 'SSP contract start'),
      billingPlan: requireBillingPlan(contract.billingPlan, 'SSP contract billing plan'),
    },
  };
}

function normaliseAudit(value: unknown, index: number): SspAuditSummary {
  const label = `SSP audit entry ${index + 1}`;
  const row = requireRecord(value, label);
  const actorUserId = nullableString(row.actorUserId, `${label} actor user id`);
  const actorServiceAccountId = nullableString(
    row.actorServiceAccountId,
    `${label} actor service account id`,
  );
  return {
    id: requireString(row.auditId, `${label} id`),
    at: requireTimestamp(row.occurredAtUtc, `${label} timestamp`),
    actor: actorUserId ?? actorServiceAccountId ?? 'system',
    action: requireString(row.actionType, `${label} action`),
    targetType: requireString(row.targetType, `${label} target type`),
    targetId: requireString(row.targetId, `${label} target id`),
    outcome: requireAuditOutcome(row.outcomeStatus, `${label} outcome`),
  };
}

function requireRecord(value: unknown, label: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, label: string): string {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return value;
}

function nullableString(value: unknown, label: string): string | null {
  if (value === null) return null;
  return requireString(value, label);
}

function requireTimestamp(value: unknown, label: string): string {
  const timestamp = requireString(value, label);
  if (!Number.isFinite(Date.parse(timestamp))) {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return timestamp;
}

function nullableTimestamp(value: unknown, label: string): string | null {
  if (value === null) return null;
  return requireTimestamp(value, label);
}

function requireNonNegativeNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return value;
}

function requireNonNegativeInteger(value: unknown, label: string): number {
  const number = requireNonNegativeNumber(value, label);
  if (!Number.isInteger(number)) {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return number;
}

function requireSspId(value: unknown, label: string): SspId {
  const id = requireUuid(value, label);
  return id as SspId;
}

function requireSsuId(value: unknown, label: string): SsuId {
  const id = requireUuid(value, label);
  return id as SsuId;
}

function requireUuid(value: unknown, label: string): string {
  const id = requireString(value, label);
  if (!UUID_PATTERN.test(id)) {
    throw new Error(`Central returned an invalid ${label}.`);
  }
  return id;
}

function requireSspStatus(value: unknown, label: string): SspStatusValue {
  const status = requireString(value, label);
  switch (status) {
    case SspStatus.Active:
    case SspStatus.Suspended:
    case SspStatus.Decommissioned:
    case SspStatus.PendingActivation:
      return status;
    default:
      throw new Error(`Central returned an invalid ${label}.`);
  }
}

function requireSsuStatus(value: unknown, label: string): SsuStatusValue {
  const status = requireString(value, label);
  switch (status) {
    case SsuStatus.Active:
    case SsuStatus.Suspended:
    case SsuStatus.Offboarded:
      return status;
    default:
      throw new Error(`Central returned an invalid ${label}.`);
  }
}

function requireBillingPlan(value: unknown, label: string): BillingPlanValue {
  const plan = requireString(value, label);
  switch (plan) {
    case BillingPlan.Standard:
    case BillingPlan.Volume:
    case BillingPlan.Government:
    case BillingPlan.Enterprise:
      return plan;
    default:
      throw new Error(`Central returned an invalid ${label}.`);
  }
}

function requireAuditOutcome(value: unknown, label: string): SspAuditSummary['outcome'] {
  const outcome = requireString(value, label).toLowerCase();
  switch (outcome) {
    case 'success':
    case 'denied':
      return outcome;
    case 'error':
      return 'failed';
    default:
      throw new Error(`Central returned an invalid ${label}.`);
  }
}
